import React from "react";
import { Alert, Button, Checkbox, Form, Input, Space, Table, Typography, message } from "antd";
import { useTranslation } from "react-i18next";
import { inviteCollaborator, passportShareDossierUrl, sharePassport } from "../../api";
import type { CollaboratorInvite, ComplianceCaseDetail, RecipientRole, ShareDelivery } from "../types";
import { RECIPIENT_ROLE_VALUES } from "../complianceLabels";
import { pushNotification } from "../ux/notificationStore";
import { useNarrowViewport } from "../ux/useNarrowViewport";
import { CopyLinkButton } from "./CopyLinkButton";
import { MobileDataCards } from "./MobileDataCards";

type Props = {
  caseData: ComplianceCaseDetail;
  onUpdated: () => void;
};

function PublicLinkRow({
  url,
  copyLabel,
  copiedToast,
}: {
  url: string;
  copyLabel: string;
  copiedToast: string;
}) {
  return (
    <Space wrap align="start" style={{ width: "100%" }}>
      <Typography.Paragraph style={{ marginBottom: 0, wordBreak: "break-all", flex: 1 }}>
        <a href={url} target="_blank" rel="noopener noreferrer">
          {url}
        </a>
      </Typography.Paragraph>
      <CopyLinkButton url={url} copyLabel={copyLabel} copiedToast={copiedToast} />
    </Space>
  );
}

function dossierUrlFromDelivery(d: ShareDelivery): string | null {
  if (d.access_token) return passportShareDossierUrl(d.access_token);
  return null;
}

export function SharePanel({ caseData, onUpdated }: Props) {
  const { t, i18n } = useTranslation();
  const isNarrow = useNarrowViewport("md");
  const [loading, setLoading] = React.useState(false);
  const [collabLoading, setCollabLoading] = React.useState(false);
  const [lastShares, setLastShares] = React.useState<ShareDelivery[]>([]);
  const [lastCollaborator, setLastCollaborator] = React.useState<CollaboratorInvite | null>(null);

  const deliveriesWithPublicLink = React.useMemo(() => {
    const fromLast = lastShares.filter((d) => d.download_url || d.access_token);
    if (fromLast.length) return fromLast;
    return caseData.share_deliveries.filter((d) => d.download_url || d.access_token);
  }, [caseData.share_deliveries, lastShares]);

  const collaboratorInvites = caseData.collaborator_invites || [];

  return (
    <Space direction="vertical" style={{ width: "100%" }}>
      <Alert type="info" showIcon message={t("compliance.share.legalDisclaimer")} />
      <Typography.Title level={5} style={{ marginBottom: 0 }}>
        {t("compliance.share.collaboratorsTitle")}
      </Typography.Title>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
        {t("compliance.share.collaboratorsHint")}
      </Typography.Paragraph>
      <Form
        layout="inline"
        onFinish={async (values: { collaboratorEmail: string }) => {
          setCollabLoading(true);
          try {
            const updated = await inviteCollaborator(caseData.id, {
              email: values.collaboratorEmail,
              locale: i18n.language,
            });
            const match =
              (updated.collaborator_invites || [])
                .filter((inv) => inv.email === values.collaboratorEmail)
                .sort(
                  (a, b) => new Date(b.expires_at).getTime() - new Date(a.expires_at).getTime()
                )[0] ?? null;
            setLastCollaborator(match);
            message.success(t("compliance.share.collaboratorInviteSent"));
            onUpdated();
          } catch {
            message.error(t("compliance.share.collaboratorInviteFailed"));
          } finally {
            setCollabLoading(false);
          }
        }}
      >
        <Form.Item
          name="collaboratorEmail"
          rules={[{ required: true, type: "email" }]}
        >
          <Input
            placeholder={t("compliance.share.collaboratorEmailPlaceholder")}
            style={{ width: 280 }}
            data-testid="collaborator-email"
          />
        </Form.Item>
        <Form.Item>
          <Button type="default" htmlType="submit" loading={collabLoading} data-testid="invite-collaborator">
            {t("compliance.share.inviteCollaborator")}
          </Button>
        </Form.Item>
      </Form>
      {lastCollaborator?.invite_url && (
        <Space direction="vertical" size={4} style={{ width: "100%" }}>
          <Typography.Text strong>{t("compliance.share.collaboratorInviteLink")}</Typography.Text>
          <PublicLinkRow
            url={lastCollaborator.invite_url}
            copyLabel={t("compliance.share.copyCollaboratorLink")}
            copiedToast={t("compliance.share.collaboratorLinkCopied")}
          />
        </Space>
      )}
      {collaboratorInvites.length > 0 && (
        <ul data-testid="collaborator-invites-list">
          {collaboratorInvites.map((inv) => (
            <li key={inv.id}>
              {inv.email} — {t("common.expires")} {new Date(inv.expires_at).toLocaleDateString()}
              {inv.accepted_at ? ` ${t("common.accepted")}` : ` ${t("common.pending")}`}
            </li>
          ))}
        </ul>
      )}
      <Typography.Paragraph type="secondary">{t("compliance.share.publicLinkHint")}</Typography.Paragraph>
      <Form
        layout="vertical"
        onFinish={async (values: { roles: RecipientRole[]; emails: string }) => {
          const emails = values.emails
            .split(/[\n,;]+/)
            .map((e) => e.trim())
            .filter(Boolean);
          const roles = values.roles || [];
          if (!emails.length || !roles.length) {
            message.warning(t("compliance.share.selectRolesAndEmails"));
            return;
          }
          setLoading(true);
          try {
            const recipients = emails.flatMap((email) =>
              roles.map((recipient_role) => ({ recipient_role, recipient_email: email }))
            );
            const updated = await sharePassport(caseData.id, {
              recipients,
              locale: i18n.language,
            });
            const withLinks = updated.share_deliveries.filter((d) => d.download_url || d.access_token);
            const newest = [...withLinks].sort(
              (a, b) => new Date(b.sent_at).getTime() - new Date(a.sent_at).getTime()
            );
            setLastShares(newest.slice(0, recipients.length));
            message.success(t("compliance.share.packageShared"));
            pushNotification({
              kind: "share_sent",
              title: t("compliance.notifications.shareSent"),
              caseId: caseData.id,
            });
            onUpdated();
          } catch {
            message.error(t("compliance.share.shareFailed"));
          } finally {
            setLoading(false);
          }
        }}
      >
        <Form.Item name="roles" label={t("compliance.share.recipientRoles")} rules={[{ required: true }]}>
          <Checkbox.Group
            options={RECIPIENT_ROLE_VALUES.map((value) => ({
              label: t(`compliance.share.recipientRolesLabels.${value}`),
              value,
            }))}
          />
        </Form.Item>
        <Form.Item name="emails" label={t("compliance.share.emailAddresses")} rules={[{ required: true }]}>
          <Input.TextArea placeholder={t("compliance.share.emailPlaceholder")} rows={3} />
        </Form.Item>
        <Button type="primary" htmlType="submit" loading={loading} disabled={!caseData.passport}>
          {t("compliance.share.sendPackage")}
        </Button>
      </Form>
      {deliveriesWithPublicLink.length > 0 && (
        <Space direction="vertical" size={8} style={{ width: "100%" }}>
          <Typography.Text strong>{t("compliance.share.publicDownloadLinks")}</Typography.Text>
          {deliveriesWithPublicLink.map((d) => {
            const dossier = dossierUrlFromDelivery(d);
            return (
              <Space key={d.id} direction="vertical" size={2} style={{ width: "100%" }}>
                <Typography.Text type="secondary">
                  {d.recipient_role} · {d.recipient_email}
                  {d.expires_at
                    ? ` · ${t("compliance.share.expiresAt", { date: new Date(d.expires_at).toLocaleString() })}`
                    : ""}
                </Typography.Text>
                {dossier ? (
                  <PublicLinkRow
                    url={dossier}
                    copyLabel={t("compliance.share.copyDossierLink")}
                    copiedToast={t("compliance.share.dossierLinkCopied")}
                  />
                ) : null}
                {d.download_url ? (
                  <PublicLinkRow
                    url={d.download_url}
                    copyLabel={t("compliance.share.copyPublicLink")}
                    copiedToast={t("compliance.share.publicLinkCopied")}
                  />
                ) : null}
              </Space>
            );
          })}
        </Space>
      )}
      {isNarrow ? (
        <MobileDataCards
          testId="share-deliveries-mobile"
          items={caseData.share_deliveries}
          rowKey={(row) => row.id}
          title={(row) => row.recipient_email}
          fields={(row) => {
            const statusKey = row.delivery_status || "queued";
            const dossier = dossierUrlFromDelivery(row);
            return [
              { label: t("common.role"), value: row.recipient_role },
              {
                label: t("compliance.share.deliveryStatus"),
                value: (
                  <Space direction="vertical" size={0}>
                    <Typography.Text>
                      {t(`compliance.share.delivery.${statusKey}`, { defaultValue: statusKey })}
                    </Typography.Text>
                    {row.delivery_detail ? (
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        {row.delivery_detail}
                      </Typography.Text>
                    ) : null}
                  </Space>
                ),
              },
              {
                label: t("common.sent"),
                value: row.sent_at ? new Date(row.sent_at).toLocaleString() : t("common.dash"),
              },
              {
                label: t("compliance.share.dossierLinkColumn"),
                value: dossier ? (
                  <PublicLinkRow
                    url={dossier}
                    copyLabel={t("compliance.share.copyDossierLink")}
                    copiedToast={t("compliance.share.dossierLinkCopied")}
                  />
                ) : (
                  t("compliance.share.noPublicLink")
                ),
              },
              {
                label: t("compliance.share.publicLinkColumn"),
                value: row.download_url ? (
                  <PublicLinkRow
                    url={row.download_url}
                    copyLabel={t("compliance.share.copyPublicLink")}
                    copiedToast={t("compliance.share.publicLinkCopied")}
                  />
                ) : (
                  t("compliance.share.noPublicLink")
                ),
              },
            ];
          }}
        />
      ) : (
      <Table
        size="small"
        rowKey="id"
        pagination={false}
        dataSource={caseData.share_deliveries}
        columns={[
          { title: t("common.role"), dataIndex: "recipient_role" },
          { title: t("common.email"), dataIndex: "recipient_email" },
          {
            title: t("compliance.share.deliveryStatus"),
            dataIndex: "delivery_status",
            render: (status: string | undefined, row: ShareDelivery) => {
              const key = status || "queued";
              return (
                <Space direction="vertical" size={0}>
                  <Typography.Text>
                    {t(`compliance.share.delivery.${key}`, { defaultValue: key })}
                  </Typography.Text>
                  {row.delivery_detail ? (
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {row.delivery_detail}
                    </Typography.Text>
                  ) : null}
                </Space>
              );
            },
          },
          {
            title: t("common.sent"),
            dataIndex: "sent_at",
            render: (v: string) => new Date(v).toLocaleString(),
          },
          {
            title: t("compliance.share.dossierLinkColumn"),
            key: "dossier",
            render: (_: unknown, row: ShareDelivery) => {
              const dossier = dossierUrlFromDelivery(row);
              return dossier ? (
                <PublicLinkRow
                  url={dossier}
                  copyLabel={t("compliance.share.copyDossierLink")}
                  copiedToast={t("compliance.share.dossierLinkCopied")}
                />
              ) : (
                t("compliance.share.noPublicLink")
              );
            },
          },
          {
            title: t("compliance.share.publicLinkColumn"),
            dataIndex: "download_url",
            render: (url: string | null) =>
              url ? (
                <PublicLinkRow
                  url={url}
                  copyLabel={t("compliance.share.copyPublicLink")}
                  copiedToast={t("compliance.share.publicLinkCopied")}
                />
              ) : (
                t("compliance.share.noPublicLink")
              ),
          },
        ]}
      />
      )}
      <Typography.Paragraph italic style={{ marginTop: 24 }}>
        {t("compliance.share.footer")}
      </Typography.Paragraph>
    </Space>
  );
}
